import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Save, FolderKanban, Calendar, DollarSign, User, Users, Activity as ActivityIcon, Timer } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import ClientHealthCard from "@/components/ClientHealthCard";
import { useAuth } from "@/lib/auth";
import { useProjectStatuses, DEFAULT_STATUS_META, CANONICAL_STATUS_KEYS } from "@/hooks/useProjectStatuses";
import MultiSelect from "@/components/ui/multi-select";
import ProjectLinksSection from "@/components/ProjectLinksSection";
import useWorkspacePerms from "@/lib/useWorkspacePerms";

// R159zz · Feb 2026 — Status list is resolved live via
// `useProjectStatuses` inside the component so per-workspace rename
// overrides show up immediately.
const HEALTHS = [
  { v: "on_track", label: "🟢 On track" },
  { v: "at_risk", label: "🟡 At risk" },
  { v: "off_track", label: "🟠 Off track" },
  { v: "blocked", label: "🔴 Blocked" },
];
const PRIORITIES = [
  { v: "low", label: "Low" },
  { v: "medium", label: "Medium" },
  { v: "high", label: "High" },
  { v: "urgent", label: "Urgent" },
];
const BILLING_TYPES = [
  { v: "fixed", label: "Fixed price" },
  { v: "hourly", label: "Hourly / T&M" },
  { v: "retainer", label: "Monthly retainer" },
];
const CURRENCIES = ["INR", "USD", "EUR", "GBP", "AUD", "CAD", "AED", "SGD"];

export default function ProjectOverviewTab({ project, projectMembers = [], onSaved }) {
  const { activeWorkspaceId } = useAuth();
  // Feb 2026 · Budget & billing is the only "financial rollup" on the
  // Overview. Non-finance PMs should still own the rest of the project
  // metadata, so we gate only this section — not the whole tab.
  const { hasPerm, isAdminRole } = useWorkspacePerms();
  const canSeeFinancials = isAdminRole
    || hasPerm("finance.view") || hasPerm("finance.manage")
    || hasPerm("workspace.admin") || hasPerm("workspace.manage");
  const { statusMeta } = useProjectStatuses(activeWorkspaceId);
  const STATUSES = CANONICAL_STATUS_KEYS.map((k) => ({ v: k, label: statusMeta[k]?.label || DEFAULT_STATUS_META[k].label }));
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [brands, setBrands] = useState([]);

  useEffect(() => {
    setForm({
      name: project?.name || "",
      description: project?.description || "",
      code: project?.code || "",
      status: project?.status || "active",
      health: project?.health || "",
      priority: project?.priority || "",
      brand_id: project?.brand_id || "",
      start_date: project?.start_date || "",
      end_date: project?.end_date || "",
      budget: project?.budget ?? "",
      currency: project?.currency || "",
      billing_type: project?.billing_type || "",
      project_manager_id: project?.project_manager_id || "",
      sponsor_id: project?.sponsor_id || "",
      estimated_hours: project?.estimated_hours ?? "",
      manual_effort_hours: project?.manual_effort_hours ?? "",
      manual_effort_note: project?.manual_effort_note || "",
    });
  }, [project?.id]);

  // Load brands filtered by this project's client so the picker only
  // shows valid options (avoids backend 400 on cross-client link).
  useEffect(() => {
    if (!project?.client_id) { setBrands([]); return; }
    api.get(`/clients/${project.client_id}/brands`)
      .then(r => setBrands((r.data && (r.data.rows || r.data)) || []))
      .catch(() => setBrands([]));
  }, [project?.client_id]);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      if (payload.budget === "") payload.budget = null;
      else if (payload.budget != null) payload.budget = Number(payload.budget);
      if (payload.estimated_hours === "") payload.estimated_hours = null;
      else if (payload.estimated_hours != null) payload.estimated_hours = Number(payload.estimated_hours);
      if (payload.manual_effort_hours === "") payload.manual_effort_hours = null;
      else if (payload.manual_effort_hours != null) payload.manual_effort_hours = Number(payload.manual_effort_hours);
      await api.patch(`/projects/${project.id}`, payload);
      toast.success("Project saved");
      onSaved && (await onSaved());
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="project-overview-tab">
      {/* Iter 193 · Client Health Card */}
      <ClientHealthCard project={project} />
      <Section icon={FolderKanban} title="Project basics">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="sm:col-span-2">
            <Field label="Name" value={form.name} onChange={(v) => set("name", v)} testid="po-name" />
          </div>
          <Field label="Project code / key" value={form.code} onChange={(v) => set("code", v)} placeholder="ACME-WEB" testid="po-code" />
        </div>
        <div>
          <Label className="block mb-1.5 text-sm font-semibold">Description</Label>
          <Textarea rows={3} value={form.description || ""} onChange={(e) => set("description", e.target.value)} data-testid="po-desc" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <SelectField label="Status" value={form.status} onChange={(v) => set("status", v)} options={STATUSES.map((s) => ({ v: s.v, label: s.label }))} testid="po-status" />
          <SelectField label="Health" value={form.health} onChange={(v) => set("health", v)} options={HEALTHS} testid="po-health" />
          <SelectField label="Priority" value={form.priority} onChange={(v) => set("priority", v)} options={PRIORITIES} testid="po-priority" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="block mb-1.5 text-sm font-semibold">Client</Label>
            <div className="h-9 px-2 border border-border bg-muted/30 rounded-none text-sm flex items-center"
                    data-testid="po-client-name">
              {project?.client?.name || "—"}
            </div>
            <div className="text-[10px] text-muted-foreground mt-1">
              Client comes from the folder this project was created under.
            </div>
          </div>
          <div>
            <Label className="block mb-1.5 text-sm font-semibold">Brand (optional)</Label>
            <select value={form.brand_id || ""}
                       onChange={(e) => set("brand_id", e.target.value)}
                       className="h-9 w-full border border-border rounded-none bg-background text-sm px-2"
                       data-testid="po-brand">
              <option value="">— no brand —</option>
              {brands.map(b => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
            <div className="text-[10px] text-muted-foreground mt-1">
              Link a brand to see this project on that brand&apos;s Projects tab.
            </div>
          </div>
        </div>
      </Section>

      <Section icon={Calendar} title="Schedule">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Start date" type="date" value={form.start_date?.slice(0, 10) || ""} onChange={(v) => set("start_date", v)} testid="po-start" />
          <Field label="Target end date" type="date" value={form.end_date?.slice(0, 10) || ""} onChange={(v) => set("end_date", v)} testid="po-end" />
        </div>
      </Section>

      <Section icon={Timer} title="Effort tracking">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <Field label="Estimated hours" type="number" value={form.estimated_hours} onChange={(v) => set("estimated_hours", v)} testid="po-est-hours" placeholder="0" />
          <Field label="Manual effort (h)" type="number" value={form.manual_effort_hours} onChange={(v) => set("manual_effort_hours", v)} testid="po-manual-effort" placeholder="0" />
          <ReadOnly label="Timesheet hours (auto)" value={project?.timesheet_hours ?? 0} testid="po-timesheet-hours" />
          <ReadOnly label="Total effort" value={project?.total_effort_hours ?? 0} testid="po-total-effort" strong />
        </div>
        <Field label="Manual effort note" value={form.manual_effort_note} onChange={(v) => set("manual_effort_note", v)} testid="po-manual-effort-note" placeholder="Where did these hours come from? e.g. Trello / prior spreadsheet…" />
        <p className="text-[11px] text-muted-foreground">
          <b>Timesheet hours</b> auto-sums every posted time entry for this project. <b>Manual effort</b> captures hours from previous systems / offline. Task-level manual effort is also rolled up into the project total.
        </p>
      </Section>

      {canSeeFinancials && (
        <Section icon={DollarSign} title="Budget & billing">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Budget" type="number" value={form.budget} onChange={(v) => set("budget", v)} testid="po-budget" placeholder="100000" />
            <SelectField label="Currency" value={form.currency} onChange={(v) => set("currency", v)} options={CURRENCIES.map((c) => ({ v: c, label: c }))} testid="po-currency" />
            <SelectField label="Billing type" value={form.billing_type} onChange={(v) => set("billing_type", v)} options={BILLING_TYPES} testid="po-billing-type" />
          </div>
        </Section>
      )}

      <Section icon={User} title="People">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <SelectField
            label="Project manager"
            value={form.project_manager_id}
            onChange={(v) => set("project_manager_id", v)}
            options={projectMembers.map((m) => ({ v: m.user_id, label: m.user?.name || m.user?.email || "—" }))}
            testid="po-pm"
          />
          <SelectField
            label="Account sponsor"
            value={form.sponsor_id}
            onChange={(v) => set("sponsor_id", v)}
            options={projectMembers.map((m) => ({ v: m.user_id, label: m.user?.name || m.user?.email || "—" }))}
            testid="po-sponsor"
          />
        </div>
      </Section>

      {/* Feb 2026 R293k · People Labels — workspace catalogue picker.
          Shown only when the workspace admin has defined at least one
          label so empty workspaces don't carry a dead section. */}
      <ProjectPeopleLabelsSection
        project={project}
        onSaved={onSaved}
      />

      {/* Feb 2026 R293n · Project Links — WhatsApp group, Drive folder,
          Figma file, GitHub repo, etc. Surfaced as service icons on the
          Project detail header and the Projects list. */}
      <ProjectLinksSection
        projectId={project.id}
        canEdit={true}
        onChanged={onSaved}
      />

      <div className="sticky bottom-2 z-10 flex justify-end pt-4">
        <Button onClick={save} disabled={busy} className="rounded-none" data-testid="po-save">
          <Save className="w-4 h-4 mr-2" /> {busy ? "Saving…" : "Save overview"}
        </Button>
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <section className="border border-border p-5 space-y-3">
      <div className="label-eyebrow inline-flex items-center gap-2">
        {Icon && <Icon className="w-3.5 h-3.5" />} {title}
      </div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, testid }) {
  return (
    <div>
      <Label className="block mb-1.5 text-sm font-semibold">{label}</Label>
      <Input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="h-10" data-testid={testid} />
    </div>
  );
}

function ReadOnly({ label, value, testid, strong }) {
  return (
    <div>
      <Label className="block mb-1.5 text-sm font-semibold">{label}</Label>
      <div className={`h-10 flex items-center px-3 border border-border bg-muted/30 font-mono text-sm ${strong ? "font-semibold" : ""}`} data-testid={testid}>
        {value}
      </div>
    </div>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <div>
      <Label className="block mb-1.5 text-sm font-semibold">{label}</Label>
      <Select value={value || "_none"} onValueChange={(v) => onChange(v === "_none" ? "" : v)}>
        <SelectTrigger className="h-10" data-testid={testid}><SelectValue placeholder="—" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="_none">—</SelectItem>
          {options.map((o) => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

/* Feb 2026 R293k · ProjectPeopleLabelsSection — picker surface for the
 * workspace People Labels catalogue. Each label becomes a sorted,
 * searchable multi-select of workspace members (any member can be
 * assigned to any label per the planning decision). Writes land on
 * PATCH /projects/{pid}/people-labels so one API call persists the
 * whole map and the backend scrubs any role_id that has since been
 * deleted. */
function ProjectPeopleLabelsSection({ project, onSaved }) {
  const { activeWorkspaceId } = useAuth();
  const [roles, setRoles] = useState([]);
  const [wsMembers, setWsMembers] = useState([]);
  const [labels, setLabels] = useState({});
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!activeWorkspaceId) return;
    Promise.all([
      api.get(`/workspaces/${activeWorkspaceId}/project-people-roles`).catch(() => ({ data: [] })),
      api.get(`/workspaces/${activeWorkspaceId}/members`).catch(() => ({ data: [] })),
    ]).then(([r, m]) => {
      setRoles(r.data || []);
      setWsMembers(m.data || []);
    });
  }, [activeWorkspaceId]);

  useEffect(() => {
    setLabels(project?.people_labels || {});
    setDirty(false);
  }, [project?.id, project?.people_labels]);

  const memberOptions = (wsMembers || [])
    .filter((m) => (m.status || "active") !== "removed" && (m.status || "active") !== "deleted")
    .map((m) => ({
      value: m.user_id,
      label: m.name || m.email || m.user?.name || m.user?.email || "User",
    }));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/projects/${project.id}/people-labels`, { labels });
      toast.success("People labels saved");
      setLabels(data.people_labels || {});
      setDirty(false);
      onSaved?.();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Save failed"));
    } finally {
      setBusy(false);
    }
  };

  if (!roles.length) {
    return (
      <Section icon={Users} title="People labels">
        <p className="text-xs text-muted-foreground">
          No People Labels defined yet. A workspace admin can set them up at{" "}
          <a href="/app/settings/people-labels" className="text-primary hover:underline" data-testid="po-people-labels-settings-link">
            Settings → Project people labels
          </a>.
        </p>
      </Section>
    );
  }

  return (
    <Section icon={Users} title="People labels">
      <div className="space-y-3">
        {roles.map((role) => {
          const selected = labels[role.id] || [];
          return (
            <div key={role.id} className="space-y-1" data-testid={`po-people-label-${role.id}`}>
              <Label className="flex items-center gap-2 text-sm font-semibold">
                <span className={`inline-block border px-2 py-0.5 text-xs ${role.color || "bg-muted/40 border-border"}`}>
                  {role.name}
                </span>
                {selected.length > 0 && (
                  <span className="text-[10px] text-muted-foreground">{selected.length} assigned</span>
                )}
              </Label>
              <MultiSelect
                options={memberOptions}
                value={selected}
                onChange={(next) => {
                  setLabels((cur) => ({ ...cur, [role.id]: next }));
                  setDirty(true);
                }}
                placeholder={`Assign ${role.name}…`}
                allLabel={`Assign ${role.name}`}
                width="w-full"
                testid={`po-people-label-select-${role.id}`}
              />
            </div>
          );
        })}
        {dirty && (
          <div className="flex justify-end">
            <Button size="sm" className="rounded-none" onClick={save} disabled={busy}
                     data-testid="po-people-labels-save">
              <Save className="w-3.5 h-3.5 mr-1" /> {busy ? "Saving…" : "Save labels"}
            </Button>
          </div>
        )}
      </div>
    </Section>
  );
}

